import { motion, AnimatePresence } from 'motion/react';
import { useExperienceStore } from '../state/useExperienceStore';
import { X, ArrowLeft, ArrowRight, ShoppingCart, Search, User, MapPin, Star, MoreHorizontal, Share2, Dumbbell, LogIn, Store, Sparkles } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../lib/AuthContext';
import { toast } from 'sonner';
import { doc, getDoc, getDocs, collection } from 'firebase/firestore';
import { db } from '../lib/firebase';
import { Shop, Product } from '../types';
import { CustomCursor } from './CustomCursor';
import { ShareModal } from './ShareModal';
import { GymOverlay } from './GymOverlay';

import { FuturisticInfographic } from './FuturisticInfographic';

export const ExperienceOverlay = () => {
  const { mode, selectedShopId, selectedProductId, selectedContinent, selectedCountry, setMode, setIntroComplete, selectShop, selectProduct, selectContinent, selectCountry, activeProductImageIndex, setActiveProductImageIndex, introComplete } = useExperienceStore();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [shop, setShop] = useState<Shop | null>(null);
  const [shopProducts, setShopProducts] = useState<Product[]>([]);
  const [product, setProduct] = useState<Product | null>(null);
  const [isShareModalOpen, setIsShareModalOpen] = useState(false);
  const [shareConfig, setShareConfig] = useState({
    url: '',
    title: '',
    description: '',
    image: '',
    shopName: '',
    productName: '',
    availableImages: [] as string[]
  });

  useEffect(() => {
    if (selectedShopId) {
      const fetchShopData = async () => {
        // Fetch Shop
        const shopSnap = await getDoc(doc(db, "shops", selectedShopId));
        if (shopSnap.exists()) setShop({ id: shopSnap.id, ...shopSnap.data() } as Shop);

        // Fetch Products
        try {
          const productsSnap = await getDocs(collection(db, "products"));
          const allProducts = productsSnap.docs.map(doc => ({ id: doc.id, ...doc.data() }) as Product);
          const filtered = allProducts.filter(p => p.shopId === selectedShopId);
          setShopProducts(filtered);
        } catch (error) {
          console.error("Error fetching products:", error);
        }
      };
      fetchShopData();
    } else {
      setShop(null);
      setShopProducts([]);
    }
  }, [selectedShopId]);

  useEffect(() => {
    if (selectedProductId) {
      const fetchProduct = async () => {
        const snap = await getDoc(doc(db, "products", selectedProductId));
        if (snap.exists()) setProduct({ id: snap.id, ...snap.data() } as Product);
      };
      fetchProduct();
    } else {
      setProduct(null);
    }
  }, [selectedProductId]);

  const handleOpenShare = () => {
    const baseUrl = window.location.origin + "/market-3d";
    let url = baseUrl;
    let title = "Mall Virtual - Experiencia 3D";
    let description = "Explora el futuro del comercio emprendedor en nuestra galería 3D.";
    let image = "https://picsum.photos/seed/mall/1200/630";
    let sName = "";
    let pName = "";
    let availableImgs: string[] = [];

    if (mode === 'STORE' && shop) {
      url += `?shop=${shop.id}`;
      title = `${shop.name} | Mall Virtual`;
      description = shop.description || description;
      image = shop.bannerURL || shop.logoURL || image;
      sName = shop.name;
    } else if (mode === 'PRODUCT' && product) {
      url += `?product=${product.id}`;
      if (shop) url += `&shop=${shop.id}`;
      title = `${product.name} | ${shop?.name || "Mall Virtual"}`;
      description = product.description || description;
      image = product.images?.[activeProductImageIndex] || product.images?.[0] || image;
      sName = shop?.name || "";
      pName = product.name;
      availableImgs = product.images || [];
    }

    setShareConfig({
      url,
      title,
      description,
      image,
      shopName: sName,
      productName: pName,
      availableImages: availableImgs
    });
    setIsShareModalOpen(true);
  };

  // if (!introComplete && mode === 'INTRO') return null;

  const [searchTerm, setSearchTerm] = useState('');
  const [allShops, setAllShops] = useState<Shop[]>([]);
  const [continentsData, setContinentsData] = useState<any[]>([]);
  const [showShops, setShowShops] = useState(false);

  useEffect(() => {
    if (mode === 'PLANET') {
      setShowShops(false);
    }
  }, [mode]);

  useEffect(() => {
    const fetchAllData = async () => {
      try {
        const snap = await getDocs(collection(db, "shops"));
        const shops = snap.docs.map(doc => ({ id: doc.id, ...doc.data() }) as Shop);
        setAllShops(shops);

        // Extract unique normalized countries from active shops
        const activeCountriesFromDb = new Set<string>();
        const CANONICAL_MAP: Record<string, string> = {
          'estados unidos': 'usa', 'usa': 'usa', 'reino unido': 'uk', 'united kingdom': 'uk',
          'uk': 'uk', 'japon': 'japón', 'españa': 'españa', 'espana': 'españa'
        };

        shops.forEach(s => {
          if (s.location?.country) {
            const raw = s.location.country.toLowerCase().trim();
            const canonical = CANONICAL_MAP[raw] || raw;
            activeCountriesFromDb.add(canonical);
          }
        });
        
        const COUNTRY_DICTIONARY: Record<string, { name: string, flag: string }> = {
          'chile': { name: 'Chile', flag: '🇨🇱' }, 'colombia': { name: 'Colombia', flag: '🇨🇴' },
          'argentina': { name: 'Argentina', flag: '🇦🇷' }, 'mexico': { name: 'México', flag: '🇲🇽' },
          'peru': { name: 'Perú', flag: '🇵🇪' }, 'usa': { name: 'USA', flag: '🇺🇸' },
          'brasil': { name: 'Brasil', flag: '🇧🇷' }, 'venezuela': { name: 'Venezuela', flag: '🇻🇪' },
          'ecuador': { name: 'Ecuador', flag: '🇪🇨' }, 'españa': { name: 'España', flag: '🇪🇸' },
          'francia': { name: 'Francia', flag: '🇫🇷' }, 'italia': { name: 'Italia', flag: '🇮🇹' },
          'alemania': { name: 'Alemania', flag: '🇩🇪' }, 'uk': { name: 'UK', flag: '🇬🇧' },
          'japón': { name: 'Japón', flag: '🇯🇵' }, 'china': { name: 'China', flag: '🇨🇳' },
          'corea': { name: 'Corea', flag: '🇰🇷' }, 'india': { name: 'India', flag: '🇮🇳' }
        };

        const continentMap: Record<string, any> = {
          'america': { id: 'america', name: 'AMÉRICA', desc: 'Innovación Emergente', countries: [] },
          'europa': { id: 'europa', name: 'EUROPA', desc: 'Diseño Clásico', countries: [] },
          'asia': { id: 'asia', name: 'ASIA', desc: 'Tecnología Futura', countries: [] },
          'other': { id: 'other', name: 'OTROS', desc: 'Mercados Globales', countries: [] }
        };

        const countryToContinent: Record<string, string> = {
          'chile': 'america', 'colombia': 'america', 'argentina': 'america', 'mexico': 'america', 
          'peru': 'america', 'usa': 'america', 'estados unidos': 'america', 'brasil': 'america',
          'venezuela': 'america', 'ecuador': 'america',
          'españa': 'europa', 'francia': 'europa', 'italia': 'europa', 'alemania': 'europa', 'uk': 'europa', 'reino unido': 'europa',
          'japón': 'asia', 'japon': 'asia', 'china': 'asia', 'corea': 'asia', 'india': 'asia'
        };

        activeCountriesFromDb.forEach(countryId => {
          const dictInfo = COUNTRY_DICTIONARY[countryId];
          const continentId = countryToContinent[countryId] || 'other';
          if (dictInfo) {
            continentMap[continentId].countries.push({ id: countryId, name: dictInfo.name, flag: dictInfo.flag });
          } else {
            continentMap[continentId].countries.push({ id: countryId, name: countryId.charAt(0).toUpperCase() + countryId.slice(1), flag: '🌐' });
          }
        });

        setContinentsData(Object.values(continentMap).filter((c: any) => c.countries.length > 0));
      } catch (e) {
        console.error("Error fetching shops and locations:", e);
      }
    };
    fetchAllData();
  }, []);

  const selectedContinentData = continentsData.find(c => c.id === selectedContinent);

  const filteredShops = allShops.filter(s => 
    s.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (s.categories && s.categories.some(cat => cat.toLowerCase().includes(searchTerm.toLowerCase()))) ||
    (s.mallZone && s.mallZone.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  return (
    <div className="fixed inset-0 pointer-events-none z-10 flex flex-col overflow-hidden">
      <CustomCursor />
      <GymOverlay />
      <AnimatePresence>
        {mode === 'INTRO' && (
          <motion.div
            key="intro-ning-h"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0, transition: { duration: 0.8, ease: "easeInOut" } }}
            className="absolute inset-0 bg-[#07070a] z-50 flex flex-col pointer-events-auto overflow-y-auto"
          >
            {/* Ambient Background Glows */}
            <div className="absolute top-[-10%] left-[-10%] w-[50%] h-[50%] bg-cyan-500/10 rounded-full blur-[120px] pointer-events-none" />
            <div className="absolute bottom-[-10%] right-[-10%] w-[50%] h-[50%] bg-purple-500/10 rounded-full blur-[120px] pointer-events-none" />
            <div className="absolute top-[40%] left-[30%] w-[40%] h-[40%] bg-blue-500/5 rounded-full blur-[150px] pointer-events-none" />

            <div className="w-full max-w-7xl mx-auto px-6 py-12 flex-1 flex flex-col justify-between relative z-10">
              {/* Header */}
              <div className="flex justify-between items-center border-b border-white/5 pb-6">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-gradient-to-br from-cyan-400 via-blue-500 to-purple-600 rounded-xl flex items-center justify-center font-bold text-white text-lg shadow-lg shadow-cyan-500/20">
                    E
                  </div>
                  <div>
                    <h2 className="text-white font-extrabold tracking-tight text-sm uppercase">EMPREMALL</h2>
                    <p className="text-cyan-400 text-[10px] tracking-[0.2em] uppercase font-mono font-medium">Portal de Entrada</p>
                  </div>
                </div>
                <div className="text-white/40 text-[10px] tracking-widest font-mono uppercase hidden sm:block">
                  Experiencia Virtual Interactiva
                </div>
              </div>

              {/* Central Title & Description */}
              <div className="my-10 text-center max-w-3xl mx-auto">
                <motion.div
                  initial={{ opacity: 0, y: -20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.8, delay: 0.2 }}
                >
                  <span className="px-4 py-1.5 rounded-full bg-white/5 border border-white/10 text-cyan-400 text-xs font-semibold tracking-wider uppercase mb-6 inline-block">
                    ✨ Bienvenido al Futuro del Comercio
                  </span>
                </motion.div>
                
                <motion.h1
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 1, delay: 0.3 }}
                  className="text-white text-5xl md:text-7xl font-black tracking-tighter leading-none uppercase italic"
                >
                  EMPREMALL <span className="text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 via-blue-400 to-purple-500">VIRTUAL</span>
                </motion.h1>
                
                <motion.p
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ duration: 1, delay: 0.5 }}
                  className="text-white/60 text-sm md:text-base max-w-xl mx-auto mt-6 leading-relaxed"
                >
                  Bienvenido a nuestra plataforma interactiva global. Elige tu destino para comenzar tu recorrido tridimensional o tu entrenamiento deportivo.
                </motion.p>
              </div>

              {/* Destination Gateways */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-5xl mx-auto w-full mb-12">
                {/* Gateway 1: The Shopping Mall */}
                <motion.div
                  initial={{ opacity: 0, x: -30 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.8, delay: 0.6 }}
                  whileHover={{ y: -5 }}
                  className="relative group bg-[#0e0e12]/60 backdrop-blur-xl border border-white/10 rounded-2xl overflow-hidden p-8 flex flex-col justify-between h-[360px] md:h-[400px] shadow-2xl hover:border-cyan-500/50 hover:shadow-cyan-500/10 transition-all duration-500 cursor-pointer"
                  onClick={() => setMode('PLANET')}
                >
                  {/* Decorative Background Image with Overlay */}
                  <div 
                    className="absolute inset-0 bg-cover bg-center opacity-10 group-hover:opacity-25 transition-all duration-700 pointer-events-none"
                    style={{ backgroundImage: `url('https://images.unsplash.com/photo-1519501025264-65ba15a82390?q=80&w=800')` }}
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#0e0e12] via-[#0e0e12]/40 to-transparent pointer-events-none" />

                  <div className="relative z-10 flex justify-between items-start">
                    <div className="p-4 bg-cyan-500/10 rounded-xl border border-cyan-500/20 text-cyan-400 group-hover:bg-cyan-500 group-hover:text-black transition-colors duration-500">
                      <Store className="w-8 h-8" />
                    </div>
                    <span className="text-white/20 text-xs font-mono font-bold tracking-widest uppercase">MALL GALERÍA 3D</span>
                  </div>

                  <div className="relative z-10 space-y-4">
                    <h3 className="text-white text-3xl font-black uppercase italic tracking-tight group-hover:text-cyan-400 transition-colors duration-300">
                      Centro Comercial
                    </h3>
                    <p className="text-white/50 text-xs md:text-sm leading-relaxed">
                      Explora la impresionante galería interactiva de tiendas virtuales. Descubre marcas de emprendedores del mundo, visita showrooms de diseño y accede a catálogos exclusivos.
                    </p>
                    <div className="pt-2">
                      <Button
                        onClick={(e) => {
                          e.stopPropagation();
                          setMode('PLANET');
                        }}
                        className="w-full py-6 bg-cyan-500 hover:bg-cyan-400 text-black font-black uppercase tracking-widest text-xs rounded-xl shadow-lg shadow-cyan-500/20 flex items-center justify-center gap-2 group-hover:scale-[1.02] transition-transform duration-300"
                      >
                        Ingresar al Mall Virtual <ArrowRight className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </motion.div>

                {/* Gateway 2: The Virtual Gym */}
                <motion.div
                  initial={{ opacity: 0, x: 30 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.8, delay: 0.7 }}
                  whileHover={{ y: -5 }}
                  className="relative group bg-[#0e0e12]/60 backdrop-blur-xl border border-white/10 rounded-2xl overflow-hidden p-8 flex flex-col justify-between h-[360px] md:h-[400px] shadow-2xl hover:border-purple-500/50 hover:shadow-purple-500/10 transition-all duration-500 cursor-pointer"
                  onClick={() => setMode('GYM')}
                >
                  {/* Decorative Background Image with Overlay */}
                  <div 
                    className="absolute inset-0 bg-cover bg-center opacity-10 group-hover:opacity-25 transition-all duration-700 pointer-events-none"
                    style={{ backgroundImage: `url('https://images.unsplash.com/photo-1534438327276-14e5300c3a48?q=80&w=800')` }}
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#0e0e12] via-[#0e0e12]/40 to-transparent pointer-events-none" />

                  <div className="relative z-10 flex justify-between items-start">
                    <div className="p-4 bg-purple-500/10 rounded-xl border border-purple-500/20 text-purple-400 group-hover:bg-purple-500 group-hover:text-black transition-colors duration-500">
                      <Dumbbell className="w-8 h-8" />
                    </div>
                    <span className="text-white/20 text-xs font-mono font-bold tracking-widest uppercase">ESTACIÓN FITNESS</span>
                  </div>

                  <div className="relative z-10 space-y-4">
                    <h3 className="text-white text-3xl font-black uppercase italic tracking-tight group-hover:text-purple-400 transition-colors duration-300">
                      Gimnasio Inteligente
                    </h3>
                    <p className="text-white/50 text-xs md:text-sm leading-relaxed">
                      Entrena de forma inteligente con nuestro atlas anatómico interactivo con IA, rutinas deportivas diseñadas por profesionales, tienda de equipamiento y entrenadores personalizados.
                    </p>
                    <div className="pt-2">
                      <Button
                        onClick={(e) => {
                          e.stopPropagation();
                          setMode('GYM');
                        }}
                        className="w-full py-6 bg-purple-600 hover:bg-purple-500 text-white font-black uppercase tracking-widest text-xs rounded-xl shadow-lg shadow-purple-500/20 flex items-center justify-center gap-2 group-hover:scale-[1.02] transition-transform duration-300"
                      >
                        Entrar al Gym Virtual <ArrowRight className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </motion.div>
              </div>

              {/* Footer */}
              <div className="flex flex-col sm:flex-row justify-between items-center border-t border-white/5 pt-6 text-[10px] text-white/30 gap-4">
                <div>&copy; 2026 EMPREMALL. Todos los derechos reservados.</div>
                <div className="flex items-center gap-4">
                  <button 
                    onClick={() => window.open('/shops', '_blank')}
                    className="hover:text-white transition-colors uppercase tracking-wider font-bold"
                  >
                    Directorio de Tiendas
                  </button>
                  <span className="w-1 h-1 bg-white/20 rounded-full" />
                  <button 
                    onClick={() => setMode('PLANET')}
                    className="hover:text-white transition-colors uppercase tracking-wider font-bold"
                  >
                    Acceso Rápido
                  </button>
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* HUD Header */}
      {mode !== 'INTRO' && (
        <header className="p-4 md:p-6 flex justify-between items-start pointer-events-auto">
        <motion.div 
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          className="flex items-center gap-2 md:gap-4 bg-black/40 backdrop-blur-md p-2 md:p-3 rounded-2xl border border-white/10"
        >
          <div className="w-8 h-8 md:w-10 md:h-10 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-xl flex items-center justify-center font-bold text-white text-xs md:text-base shrink-0">
            M
          </div>
          <div className="hidden sm:block">
            <h1 className="text-white font-bold leading-none text-xs md:text-sm lg:text-base">Bienvenido</h1>
            <p className="text-white/50 text-[8px] md:text-[10px] uppercase tracking-widest mt-0.5 md:mt-1">Experience</p>
          </div>
        </motion.div>

        <motion.div 
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          className="flex items-center gap-1 md:gap-3 bg-black/20 backdrop-blur-md p-1.5 md:p-2 rounded-2xl border border-white/10"
        >
          <Button 
            onClick={handleOpenShare}
            variant="ghost" 
            size="icon" 
            className="text-white hover:bg-white/10 rounded-xl h-8 w-8 md:h-10 md:w-10 shrink-0"
          >
            <Share2 className="h-4 w-4 md:h-5 md:w-5" />
          </Button>
          <Button variant="ghost" size="icon" className="text-white hover:bg-white/10 rounded-xl h-8 w-8 md:h-10 md:w-10 shrink-0">
            <Search className="h-4 w-4 md:h-5 md:w-5" />
          </Button>
          <Button 
            onClick={() => {
              if (user) {
                navigate('/profile');
              } else {
                navigate('/login');
              }
            }}
            variant="ghost" 
            size="icon" 
            className="text-white hover:bg-white/10 rounded-xl h-8 w-8 md:h-10 md:w-10 shrink-0"
          >
            {user ? <User className="h-4 w-4 md:h-5 md:w-5" /> : <LogIn className="h-4 w-4 md:h-5 md:w-5" />}
          </Button>
          <Button variant="ghost" size="icon" className="text-white hover:bg-white/10 rounded-xl h-8 w-8 md:h-10 md:w-10 shrink-0 relative">
            <ShoppingCart className="h-4 w-4 md:h-5 md:w-5" />
            <span className="absolute top-0.5 right-0.5 w-3.5 h-3.5 md:w-4 md:h-4 bg-orange-500 rounded-full text-[8px] md:text-[10px] flex items-center justify-center font-bold text-white">2</span>
          </Button>
          <Button 
            onClick={() => window.open('/shops', '_blank')}
            variant="ghost" 
            size="icon" 
            className="text-white hover:bg-red-500/20 hover:text-red-400 rounded-xl h-8 w-8 md:h-10 md:w-10 shrink-0 border border-white/5"
            title="Salir"
          >
            <X className="h-4 w-4 md:h-5 md:w-5" />
          </Button>
        </motion.div>
      </header>
      )}

      <ShareModal 
        isOpen={isShareModalOpen}
        onOpenChange={setIsShareModalOpen}
        originalUrl={shareConfig.url}
        defaultTitle={shareConfig.title}
        defaultDescription={shareConfig.description}
        defaultImage={shareConfig.image}
        shopName={shareConfig.shopName}
        productName={shareConfig.productName}
        availableImages={shareConfig.availableImages}
      />

      {/* Main Content Area */}
      <main className="flex-1 relative overflow-hidden">
        <AnimatePresence mode="wait">
          {mode === 'PLANET' && (
            <motion.div
              key="planet-hud"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="absolute inset-0 flex flex-col items-center justify-center p-6 text-center pointer-events-none"
            >
              {/* Fading Title */}
              <motion.div
                initial={{ opacity: 1, scale: 1 }}
                animate={{ 
                  opacity: showShops ? 0 : 1,
                  scale: showShops ? 0.9 : 1,
                  y: showShops ? -20 : 0
                }}
                transition={{ duration: 1.5, ease: "easeInOut" }}
                onAnimationComplete={() => {
                   if (!showShops) {
                     setTimeout(() => setShowShops(true), 1500);
                   }
                }}
                className="max-w-2xl"
              >
                <h2 className="text-6xl md:text-8xl font-black text-white italic tracking-tighter uppercase leading-tight mix-blend-difference">
                  Bienvenido
                </h2>
                <div className="w-24 h-1 bg-cyan-500 mx-auto mt-4 shadow-[0_0_20px_#06b6d4]" />
              </motion.div>

              {/* Shop Carousel */}
              {showShops && (
                <motion.div
                  initial={{ opacity: 0, y: 50 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 1, ease: "easeOut" }}
                  className="absolute inset-0 flex flex-col items-center justify-center bg-black/20 backdrop-blur-sm pointer-events-auto"
                >
                  <div className="w-full max-w-6xl px-4 flex flex-col gap-8">
                    <div className="flex flex-col md:flex-row justify-between items-end gap-4 overflow-hidden p-1">
                       <motion.div 
                        initial={{ x: -20, opacity: 0 }} 
                        animate={{ x: 0, opacity: 1 }}
                        className="text-left"
                       >
                         <h3 className="text-3xl font-black text-white uppercase italic tracking-tighter shadow-sm">Apoya Emprendedores</h3>
                         <p className="text-cyan-400 text-[10px] font-bold uppercase tracking-[0.4em] mt-1">Directorio Global Activo</p>
                       </motion.div>

                       <motion.div 
                         initial={{ x: 20, opacity: 0 }} 
                         animate={{ x: 0, opacity: 1 }}
                         className="relative w-full max-w-md"
                       >
                         <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-white/40" />
                         <input 
                           type="text"
                           placeholder="BUSCAR POR NOMBRE O CATEGORÍA..."
                           value={searchTerm}
                           onChange={(e) => setSearchTerm(e.target.value)}
                           className="w-full bg-white/5 border border-white/10 px-12 py-4 rounded-full text-white text-[10px] font-bold uppercase tracking-widest focus:outline-none focus:border-cyan-500/50 transition-all placeholder:text-white/20"
                         />
                       </motion.div>
                    </div>

                    <div className="relative group/carousel">
                      <div id="shop-carousel" className="flex gap-6 overflow-x-auto pb-12 pt-4 snap-x no-scrollbar scroll-smooth">
                        {filteredShops.length > 0 ? filteredShops.map((s, idx) => (
                            <motion.button
                              key={s.id}
                              initial={{ opacity: 0, scale: 0.9, x: 50 }}
                              animate={{ opacity: 1, scale: 1, x: 0 }}
                              transition={{ delay: idx * 0.1 }}
                              onClick={(e) => {
                                e.stopPropagation();
                                selectShop(s.id);
                              }}
                              className="min-w-[280px] md:min-w-[320px] aspect-[3/4] bg-white/5 backdrop-blur-xl border border-white/10 rounded-2xl overflow-hidden cursor-pointer snap-center group relative shadow-2xl text-left appearance-none"
                            >
                              <img 
                                src={s.bannerURL || `https://picsum.photos/seed/${s.id}/600/800`} 
                                className="w-full h-full object-cover opacity-50 group-hover:opacity-80 transition-all duration-700 group-hover:scale-110 pointer-events-none"
                                alt={s.name}
                              />
                              <div className="absolute inset-0 bg-gradient-to-t from-black via-black/20 to-transparent p-8 flex flex-col justify-end pointer-events-none">
                                <span className="text-cyan-400 text-[10px] font-bold uppercase tracking-widest mb-2 block">{(s.categories && s.categories[0]) || s.mallZone || 'Emprendimiento'}</span>
                                <h4 className="text-2xl font-black text-white uppercase italic leading-none group-hover:tracking-wider transition-all">{s.name}</h4>
                                <div className="flex items-center gap-2 mt-4 text-white/40 group-hover:text-cyan-400 transition-colors">
                                  <MapPin className="w-3 h-3" />
                                  <span className="text-[10px] font-bold uppercase tracking-widest">{s.location?.city || 'Global'}, {s.location?.country || 'Global'}</span>
                                </div>
                                <div className="mt-6 bg-white text-black rounded-none font-black uppercase text-[10px] tracking-widest h-10 flex items-center justify-center transform translate-y-10 group-hover:translate-y-0 opacity-0 group-hover:opacity-100 transition-all duration-500">
                                   Explorar Tienda
                                </div>
                              </div>
                            </motion.button>
                        )) : (
                          <div className="w-full py-20 text-center text-white/20 uppercase tracking-[0.5em] font-black text-xs">
                            No se encontraron resultados
                          </div>
                        )}
                      </div>

                      {/* Navigation Arrows */}
                      <div className="absolute top-1/2 -left-4 -translate-y-1/2 hidden md:block group-hover/carousel:opacity-100 opacity-0 transition-opacity">
                        <Button 
                          variant="ghost" 
                          size="icon"
                          onClick={() => document.getElementById('shop-carousel')?.scrollBy({ left: -400, behavior: 'smooth' })}
                          className="bg-black/80 text-white rounded-full h-12 w-12 border border-white/10 hover:bg-cyan-500 hover:text-white"
                        >
                          <ArrowLeft className="w-6 h-6" />
                        </Button>
                      </div>
                      <div className="absolute top-1/2 -right-4 -translate-y-1/2 hidden md:block group-hover/carousel:opacity-100 opacity-0 transition-opacity">
                        <Button 
                          variant="ghost" 
                          size="icon"
                          onClick={() => document.getElementById('shop-carousel')?.scrollBy({ left: 400, behavior: 'smooth' })}
                          className="bg-black/80 text-white rounded-full h-12 w-12 border border-white/10 hover:bg-cyan-500 hover:text-white"
                        >
                          <ArrowRight className="w-6 h-6" />
                        </Button>
                      </div>
                    </div>

                    <div className="flex flex-col gap-3 w-full">
                      <Button 
                        onClick={() => useExperienceStore.getState().setMode('CONTINENT')}
                        className="px-8 py-6 bg-white text-black font-black uppercase tracking-widest hover:bg-cyan-500 hover:text-white transition-all transform hover:scale-105 rounded-none h-auto w-full group"
                      >
                         Ubica lo que necesites <ArrowRight className="ml-2 w-4 h-4 group-hover:translate-x-1 transition-transform" />
                      </Button>
                      <Button 
                        onClick={() => {
                          if (user) {
                            useExperienceStore.getState().setMode('GYM');
                          } else {
                            toast.info("Inicia sesión para entrar al GYM", {
                              description: "Debes estar registrado para acceder a nuestra zona virtual de entrenamiento.",
                              action: {
                                label: "Ir a Login",
                                onClick: () => window.location.href = '/login?redirect=gym'
                              }
                            });
                          }
                        }}
                        className="px-8 py-6 bg-black text-white border border-white/20 font-black uppercase tracking-widest hover:bg-white hover:text-black transition-all transform hover:scale-105 rounded-none h-auto w-full group"
                      >
                         ENTRAR EL GYM <ArrowRight className="ml-2 w-4 h-4 group-hover:translate-x-1 transition-transform" />
                      </Button>
                      <Button 
                        onClick={() => window.open('/shops', '_blank')}
                        variant="outline"
                        className="px-8 py-6 border-white/20 text-white font-black uppercase tracking-widest hover:bg-cyan-500 hover:border-cyan-500 hover:text-white transition-all transform hover:scale-105 rounded-none h-auto w-full group"
                      >
                        Ver todos
                      </Button>
                    </div>
                  </div>
                </motion.div>
              )}
            </motion.div>
          )}

          {mode === 'CONTINENT' && (
            <motion.div
              key="continent-selector"
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 1.05 }}
              className="absolute inset-0 flex items-center justify-center p-4 py-12 md:p-24 pointer-events-auto overflow-y-auto"
            >
              <div className="w-full max-w-5xl bg-black/80 backdrop-blur-[40px] border border-white/10 rounded-none overflow-hidden flex flex-col md:flex-row shadow-[0_0_100px_rgba(0,0,0,0.9)] relative">
                {/* Neon Accent Border (Top) */}
                <div className="absolute top-0 left-0 right-0 h-0.5 bg-cyan-500 shadow-[0_0_20px_rgba(0,255,255,0.8)]" />
                
                <div className="md:w-2/5 p-8 md:p-14 bg-white/[0.02] flex flex-col justify-center border-b md:border-b-0 md:border-r border-white/10">
                  <div className="mb-8">
                    <div className="flex items-center gap-4 mb-4">
                      <span className="text-cyan-400 font-mono tracking-[0.6em] text-[10px] uppercase block animate-pulse">System_Online</span>
                      <div className="flex gap-1">
                        <motion.div animate={{ opacity: [0.2, 1, 0.2] }} transition={{ repeat: Infinity, duration: 2 }}>
                          <ArrowRight className="w-3.5 h-3.5 text-cyan-400" />
                        </motion.div>
                        <motion.div animate={{ opacity: [0.2, 1, 0.2] }} transition={{ repeat: Infinity, duration: 2, delay: 0.5 }}>
                          <ArrowRight className="w-3.5 h-3.5 text-cyan-400" />
                        </motion.div>
                      </div>
                    </div>
                    <h2 className="text-4xl md:text-6xl font-black text-white tracking-tighter uppercase italic leading-[0.9]">
                      Global <br/><span className="text-cyan-400">Gateway</span>
                    </h2>
                  </div>
                  <div className="space-y-8">
                    <p className="text-white/40 text-xs leading-relaxed uppercase tracking-widest font-medium">
                      Select your destination node to synchronize with regional commerce networks.
                    </p>
                    
                           <div className="flex items-center gap-4 pt-4">
                       <button 
                         onClick={() => setMode('INTRO')}
                         className="group flex items-center gap-2 text-white/40 hover:text-white transition-all uppercase tracking-[0.3em] text-[10px] font-black"
                       >
                         <ArrowLeft className="w-3.5 h-3.5 group-hover:-translate-x-1 transition-transform" />
                         <span>Volver</span>
                       </button>
                       <div className="w-px h-4 bg-white/10" />
                       <button 
                         onClick={() => {
                           if (continentsData.length > 0) {
                             selectContinent(continentsData[0].id);
                           }
                         }}
                         className="group flex items-center gap-2 text-cyan-400/70 hover:text-cyan-400 transition-all uppercase tracking-[0.3em] text-[10px] font-black"
                       >
                         <span>Continuar</span>
                         <ArrowRight className="w-3.5 h-3.5 group-hover:translate-x-1 transition-transform" />
                       </button>
                       <div className="w-px h-4 bg-white/10" />
                       <button 
                         onClick={() => window.open('/shops', '_blank')}
                         className="group flex items-center gap-2 text-white/20 hover:text-white transition-all uppercase tracking-[0.3em] text-[10px] font-black"
                       >
                         <span>Saltar</span>
                         <ArrowRight className="w-3.5 h-3.5 group-hover:translate-x-1 transition-transform" />
                       </button>
                    </div>

                    <div className="flex gap-2">
                       <div className="w-2 h-2 bg-cyan-500 rounded-full" />
                       <div className="w-2 h-2 bg-white/20 rounded-full" />
                       <div className="w-2 h-2 bg-white/20 rounded-full" />
                    </div>
                  </div>
                </div>
                <div className="flex-1 p-6 md:p-14 grid grid-cols-1 sm:grid-cols-2 gap-6 bg-black/40">
                  {continentsData.map((c) => (
                    <button
                      key={c.id}
                      onClick={() => selectContinent(c.id)}
                      className="group p-8 bg-white/[0.02] hover:bg-white/[0.08] border border-white/5 hover:border-cyan-500/50 transition-all text-left relative overflow-hidden group/btn"
                    >
                      {/* Corner Accents */}
                      <div className="absolute top-0 right-0 w-8 h-8 border-t border-r border-white/0 group-hover:border-cyan-500/40 transition-all" />
                      
                      <div className="relative z-10">
                        <span className="text-[10px] text-cyan-400/60 font-mono mb-2 block tracking-widest">NODE_{c.id.toUpperCase()}</span>
                        <h3 className="text-2xl font-black text-white italic mb-1 transition-all group-hover:tracking-wider">{c.name}</h3>
                        <p className="text-[9px] text-white/30 uppercase tracking-[0.3em] font-bold mt-3 leading-none">{c.desc}</p>
                      </div>

                      {/* Hover Flash Effect */}
                      <div className="absolute inset-0 bg-gradient-to-tr from-cyan-500/0 via-cyan-500/0 to-cyan-500/5 opacity-0 group-hover:opacity-100 transition-opacity" />
                    </button>
                  ))}
                </div>
              </div>
            </motion.div>
          )}

          {mode === 'COUNTRY' && (
            <motion.div
              key="country-selector"
              initial={{ opacity: 0, x: 100 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -100 }}
              className="absolute inset-0 flex items-center justify-center p-4 py-12 md:p-24 pointer-events-auto overflow-y-auto"
            >
               <div className="w-full max-w-5xl bg-black/80 backdrop-blur-[40px] border border-white/10 rounded-none overflow-hidden shadow-[0_0_100px_rgba(0,0,0,0.9)] relative">
                  <div className="p-8 md:p-12 border-b border-white/10 flex flex-col md:flex-row justify-between items-start md:items-center bg-white/[0.02] gap-6">
                    <div>
                      <span className="text-cyan-400 font-mono tracking-[0.4em] text-[10px] uppercase mb-2 block">Region Selection</span>
                      <h2 className="text-3xl md:text-5xl font-black text-white italic uppercase tracking-tighter">
                        {selectedContinentData?.name || 'PAÍSES'}
                      </h2>
                    </div>
                    <div className="flex items-center gap-4">
                      <button 
                        onClick={() => selectContinent(null)}
                        className="group flex items-center gap-2 text-white/40 hover:text-white transition-all uppercase tracking-[0.3em] text-[10px] font-black"
                      >
                        <ArrowLeft className="w-3.5 h-3.5 group-hover:-translate-x-1 transition-transform" />
                        <span>Volver</span>
                      </button>
                      <div className="w-px h-4 bg-white/10" />
                      <button 
                         onClick={() => {
                           if (selectedContinentData?.countries && selectedContinentData.countries.length > 0) {
                             selectCountry(selectedContinentData.countries[0].id);
                             setIntroComplete(true);
                           }
                         }}
                         className="group flex items-center gap-2 text-cyan-400/70 hover:text-cyan-400 transition-all uppercase tracking-[0.3em] text-[10px] font-black"
                       >
                         <span>Continuar</span>
                         <ArrowRight className="w-3.5 h-3.5 group-hover:translate-x-1 transition-transform" />
                       </button>
                       <div className="w-px h-4 bg-white/10" />
                       <button 
                         onClick={() => window.open('/shops', '_blank')}
                         className="group flex items-center gap-2 text-white/20 hover:text-white transition-all uppercase tracking-[0.3em] text-[10px] font-black"
                       >
                         <span>Saltar</span>
                         <ArrowRight className="w-3.5 h-3.5 group-hover:translate-x-1 transition-transform" />
                       </button>
                    </div>
                  </div>
                  <div className="p-8 md:p-12 grid grid-cols-2 md:grid-cols-3 gap-6">
                    {selectedContinentData?.countries.map((country) => (
                      <button
                        key={country.id}
                        onClick={() => {
                          selectCountry(country.id);
                          setIntroComplete(true);
                        }}
                        className="group p-8 bg-white/5 hover:bg-white/10 border border-white/5 hover:border-cyan-500/50 transition-all text-center relative overflow-hidden"
                      >
                        <div className="text-7xl group-hover:scale-125 transition-transform duration-500">
                           {country.flag}
                         </div>
                         <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-0 h-1 bg-cyan-500 group-hover:w-full transition-all duration-500" />
                      </button>
                    ))}
                  </div>
               </div>
            </motion.div>
          )}

          {mode === 'HUB' && (
            <motion.div
              key="hub-nav-special"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              className="absolute top-1/2 right-8 -translate-y-1/2 flex flex-col items-center gap-6 pointer-events-auto"
            >
              <div className="h-20 w-px bg-white/10" />
              <button 
                onClick={() => setMode('GYM')}
                className="group relative flex items-center justify-center w-14 h-14 bg-cyan-500/10 border border-cyan-500/20 hover:bg-cyan-500 hover:border-cyan-500 rounded-2xl transition-all duration-500"
              >
                <Dumbbell className="w-6 h-6 text-cyan-400 group-hover:text-black transition-colors" />
                <span className="absolute right-full mr-4 opacity-0 group-hover:opacity-100 transition-opacity bg-black/80 backdrop-blur-xl px-4 py-2 border border-white/10 text-[10px] font-black uppercase tracking-[0.4em] whitespace-nowrap text-cyan-400">
                  Gym Virtual
                </span>
              </button>
              <div className="h-20 w-px bg-white/10" />
            </motion.div>
          )}

          {mode === 'HUB' && (
            <motion.div
              key="hub-navigation"
              initial={{ opacity: 0, y: 50 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 50 }}
              className="absolute bottom-10 left-1/2 -translate-x-1/2 w-full max-w-sm px-6 pointer-events-auto"
            >
              <div className="bg-black/60 backdrop-blur-2xl border border-white/10 p-2 rounded-full flex items-center justify-between shadow-[0_0_50px_rgba(0,0,0,0.5)]">
                <Button 
                  variant="ghost" 
                  size="icon"
                  onClick={() => selectCountry(null)}
                  className="rounded-full h-12 w-12 text-white hover:bg-white/10 group"
                >
                  <ArrowLeft className="w-5 h-5 transition-transform group-hover:-translate-x-1" />
                </Button>
                
                <div className="flex-1 text-center">
                  <span className="text-[10px] text-cyan-400 font-mono tracking-[0.3em] font-bold block">GALLERY_SYNC</span>
                  <span className="text-white text-xs font-black uppercase tracking-widest italic">{selectedCountry?.toUpperCase() || 'HUB'} AXIS</span>
                </div>

                <Button 
                  variant="ghost" 
                  size="icon"
                  onClick={() => window.open('/shops', '_blank')}
                  className="rounded-full h-12 w-12 text-white hover:bg-white/10 group bg-white/5 border border-white/10"
                  title="Virtual Store"
                >
                  <ShoppingCart className="w-5 h-5 transition-transform group-hover:scale-110" />
                </Button>
              </div>
              
              {/* Optional labels for side navigation if needed later */}
              <div className="flex justify-between mt-4 px-4 text-[8px] text-white/30 uppercase tracking-[0.5em] font-bold font-mono">
                 <span>Prev_Node</span>
                 <span>Network_Active</span>
              </div>
            </motion.div>
          )}

          {mode === 'HUB' && (
            <motion.div
              key="hub-controls-guide"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 2 }}
              className="absolute bottom-32 left-1/2 -translate-x-1/2 flex gap-8 text-white/40 text-[9px] font-black uppercase tracking-[0.3em]"
            >
              <div className="flex items-center gap-2">
                <span className="bg-white/10 px-2 py-1 rounded border border-white/10 text-white">WASD</span>
                <span>Moverse</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="bg-white/10 px-2 py-1 rounded border border-white/10 text-white">CLICK + ARRASTRAR</span>
                <span>Mirar</span>
              </div>
            </motion.div>
          )}

          {mode === 'HUB' && (
            <motion.div
              key="hub-sidebar"
              initial={{ opacity: 0, x: -100 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -100 }}
              className="absolute top-1/2 left-8 -translate-y-1/2 flex flex-col gap-8 pointer-events-none"
            >
               <div className="h-40 w-px bg-gradient-to-b from-transparent via-cyan-500 to-transparent opacity-30" />
               <div className="text-white/20 text-[10px] [writing-mode:vertical-lr] rotate-180 font-black uppercase tracking-[0.8em]">
                  Virtual_Gallery_v4.0
               </div>
               <div className="h-40 w-px bg-gradient-to-b from-transparent via-cyan-500 to-transparent opacity-30" />
            </motion.div>
          )}

          {mode === 'HUB' && (
            <motion.div
              key="hub"
              initial={{ opacity: 0, scale: 0.98 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 1.05 }}
              className="absolute inset-0 flex flex-col items-center justify-center text-center space-y-8 pointer-events-none p-6"
            >
              <div className="max-w-3xl">
                <motion.h2 
                  initial={{ y: 50, opacity: 0 }}
                  animate={{ y: 0, opacity: 1 }}
                  transition={{ delay: 0.2 }}
                  className="text-6xl md:text-8xl font-extrabold text-white tracking-tighter leading-[0.9] uppercase italic"
                >
                  {selectedCountry ? selectedCountry : 'Virtual'} <br/> Gallery
                </motion.h2>
                <motion.p 
                  initial={{ y: 20, opacity: 0 }}
                  animate={{ y: 0, opacity: 0.4 }}
                  transition={{ delay: 0.4 }}
                  className="text-white mt-8 text-sm md:text-base tracking-[0.3em] font-medium uppercase"
                >
                  Curated local experiences / Spring Summer 2026
                </motion.p>
              </div>
              
              <motion.div 
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 1 }}
                className="absolute bottom-12 flex flex-col items-center gap-4"
              >
                <div className="w-px h-12 bg-gradient-to-b from-white/0 to-white/40" />
                <span className="text-[10px] text-white/40 uppercase tracking-[0.5em] font-bold">Select a module to enter</span>
              </motion.div>
            </motion.div>
          )}

          {mode === 'STORE' && (
            <motion.div
              key="store"
              initial={{ opacity: 0, x: -50 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -50 }}
              className="absolute inset-y-0 left-0 w-full md:max-w-md bg-black/90 md:bg-black/80 backdrop-blur-3xl border-r border-white/5 p-6 md:p-8 pt-20 md:pt-24 pointer-events-auto overflow-y-auto"
            >
              <div className="space-y-8 md:space-y-12">
                <Button 
                  onClick={() => selectShop(null)}
                  variant="ghost" 
                  className="text-white/40 p-0 h-auto hover:bg-transparent hover:text-white group text-[10px] uppercase tracking-[0.4em] font-black"
                >
                  <ArrowLeft className="mr-2 h-4 w-4 transition-transform group-hover:-translate-x-1" /> Back to hub
                </Button>
                
                <div className="space-y-4 md:space-y-6">
                  <h3 className="text-3xl md:text-5xl font-black text-white tracking-tighter leading-none uppercase italic break-words">{shop?.name || "CARGANDO..."}</h3>
                  <p className="text-white/40 text-[10px] md:text-sm leading-loose tracking-wide">
                    {shop?.description || "Curated selection from our exclusive partner store. Designed with precision and crafted for the modern individual."}
                  </p>
                </div>

                <div className="space-y-6">
                  <Button 
                    onClick={() => window.open('/shops', '_blank')}
                    className="w-full bg-cyan-600/20 text-cyan-400 border border-cyan-500/50 hover:bg-cyan-600/40 rounded-none h-12 font-black uppercase tracking-widest text-[10px]"
                  >
                    Ir a EmpreMarket
                  </Button>
                  
                  <h4 className="text-[10px] font-black text-white/20 uppercase tracking-[0.5em]">Catalogue</h4>
                  <div className="grid grid-cols-1 gap-4 md:gap-6 pb-20">
                    {shopProducts.length > 0 ? shopProducts.map((p, idx) => (
                      <motion.div 
                        key={p.id} 
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: idx * 0.1 }}
                        onClick={() => selectProduct(p.id)}
                        className="flex gap-4 md:gap-6 items-center group cursor-pointer"
                      >
                        <div className="w-20 h-28 md:w-24 md:h-32 bg-white/5 overflow-hidden shrink-0 border border-white/5 group-hover:border-cyan-500/30 transition-colors">
                           <img 
                            src={p.images?.[0] || `https://picsum.photos/seed/${p.id}/300/400`} 
                            className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110 opacity-60 group-hover:opacity-100" 
                            alt={p.name}
                          />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-[8px] md:text-[10px] text-cyan-400/60 uppercase tracking-widest font-bold">{p.category || 'Producto'}</p>
                          <p className="text-xs md:text-sm font-bold text-white mt-1 uppercase tracking-tight truncate group-hover:text-cyan-400 transition-colors">{p.name}</p>
                          <p className="text-[10px] md:text-xs text-white/50 mt-1 md:mt-2 italic tracking-widest">
                            {p.priceToAgree ? 'Consulte Precio' : `$${p.price?.toLocaleString() || '0'}`}
                          </p>
                        </div>
                      </motion.div>
                    )) : (
                      <div className="py-12 text-center text-white/10 uppercase tracking-[0.4em] font-black text-[10px] border border-dashed border-white/5">
                        Sin catálogo disponible
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </motion.div>
          )}
          {mode === 'PRODUCT' && (
            <motion.div
              key="product"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="absolute inset-0 flex flex-col pointer-events-none"
            >
              <div className="flex-1 flex justify-center md:justify-end p-4 md:p-8 overflow-y-auto">
                <motion.div 
                  initial={{ x: 50, opacity: 0 }}
                  animate={{ x: 0, opacity: 1 }}
                  className="w-full max-w-sm glass-card p-6 md:p-10 rounded-none pointer-events-auto h-fit my-auto md:mt-0 shadow-2xl space-y-6 md:space-y-8"
                >
                  <Button 
                    onClick={() => selectProduct(null)}
                    variant="ghost" 
                    className="text-white/40 p-0 h-auto hover:bg-transparent hover:text-white group text-[10px] uppercase tracking-[0.4em] font-black"
                  >
                    <ArrowLeft className="mr-2 h-4 w-4 transition-transform group-hover:-translate-x-1" /> Close View
                  </Button>
                  
                  <div className="space-y-3 md:space-y-4">
                    <p className="text-[10px] text-white/30 uppercase tracking-[0.5em] font-bold italic">Spec Sheet</p>
                    <h3 className="text-2xl md:text-3xl font-black text-white tracking-tighter uppercase italic break-words">{product?.name || "PRODUCT"}</h3>
                    <p className="text-white/40 text-[10px] md:text-xs leading-loose">
                      {product?.description || "Inspirado en el diseño minimalista, este producto combina funcionalidad y estética premium."}
                    </p>
                  </div>
                  
                  <div className="pt-4 md:pt-8 space-y-4 md:space-y-6">
                    <div className="flex justify-between items-end border-b border-white/10 pb-4">
                      <span className="text-white/30 text-[10px] uppercase tracking-widest font-bold">Retail Price</span>
                      <span className="text-xl md:text-2xl font-black text-white">${product?.price?.toLocaleString() || "12,990"}</span>
                    </div>
                    <Button className="w-full bg-white text-black hover:bg-white/90 rounded-none h-12 md:h-14 font-black uppercase tracking-widest text-xs">
                      Purchase Now
                    </Button>
                  </div>
                </motion.div>
              </div>

              {/* Bottom Centered Carousel */}
              <motion.div 
                initial={{ y: 50, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ delay: 0.3 }}
                className="p-8 md:p-12 flex justify-center items-center pointer-events-auto"
              >
                <div className="flex gap-2 md:gap-4">
                  {(product?.images && product.images.length > 0 ? product.images : [0]).map((_, i) => (
                    <motion.button
                      key={i}
                      onClick={() => setActiveProductImageIndex(i)}
                      className={`w-1 transition-all duration-500 ${
                        activeProductImageIndex === i 
                          ? 'bg-white h-10 md:h-16' 
                          : 'bg-white/20 h-6 md:h-12'
                      }`}
                    />
                  ))}
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </main>

      {/* Mini Controls */}
      <footer className="p-6 pointer-events-none flex justify-center">
        <button 
          onClick={() => window.open('/shops', '_blank')}
          className="bg-black/40 backdrop-blur-xl border border-white/10 px-6 py-2 rounded-full flex items-center gap-2 pointer-events-auto transition-all hover:bg-cyan-500/20 hover:border-cyan-500/50 hover:scale-105 active:scale-95"
        >
          <div className="w-2 h-2 rounded-full bg-cyan-400 shadow-[0_0_8px_#22d3ee]" />
          <span className="text-[10px] font-bold text-white uppercase tracking-tighter">
            Ir a Tienda Tradicional (Salir)
          </span>
        </button>
      </footer>
    </div>
  );
};
